import React, { useContext, useEffect, useMemo } from "react";
import CountdownContent from "../../Components/Cart/CountdownContent";
import { Box, Button, Center, SegmentedControl } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import Asterisk from "./Icons/Asterisk";
import CardIcon from "./Icons/CardIcon";
import InvoiceIcon from "./Icons/InvoiceIcon";
import AddDiscountGiftCertificate from "./AddDiscountGiftCertificate";
import { AppContext } from "../../context";
import {
  GIFTCERTIFICATE,
  INVOICE,
  KLARNA,
  NETSEASY,
  SWEDBANKPAY,
  SWISH,
} from "../../Components/Payments/constants";
import { useSelector } from "react-redux";
import User from "../../Components/User";

const Icon = ({
  method,
  posId,
  chosen,
  language,
  coverTotalCost,
  classes,
  config,
}) => {
  if (method === KLARNA)
    return (
      <img
        className={coverTotalCost && classes.grayedOut}
        src={`${config.baseUrl}/ee-static/checkout/pos-${posId}/images/klarna.svg`}
        style={{ width: "100%", maxHeight: "1.65rem" }}
        alt="Klarna"
        aria-hidden="true"
      />
    );
  if (method === NETSEASY) return <CardIcon language={language} />;
  if (method === SWEDBANKPAY && !chosen)
    return (
      <img
        className={coverTotalCost && classes.grayedOut}
        src={`${config.baseUrl}/ee-static/checkout/pos-${posId}/images/swedbankpay.svg`}
        style={{ width: "100%", maxHeight: "1.65rem" }}
        alt="Swish"
        aria-hidden="true"
      />
    );
  if (method === SWEDBANKPAY && chosen)
    return (
      <img
        className={coverTotalCost && classes.grayedOut}
        src={`${config.baseUrl}/ee-static/checkout/pos-${posId}/images/swedbankpay-white.svg`}
        style={{ width: "100%", maxHeight: "1.65rem" }}
        alt="Swish"
        aria-hidden="true"
      />
    );
  if (method === SWISH && !chosen)
    return (
      <img
        className={coverTotalCost && classes.grayedOut}
        src={`${config.baseUrl}/ee-static/checkout/pos-${posId}/images/swish.svg`}
        style={{ width: "100%", maxHeight: "2rem" }}
        alt="Swish"
        aria-hidden="true"
      />
    );
  if (method === SWISH && chosen)
    return (
      <img
        className={coverTotalCost && classes.grayedOut}
        src={`${config.baseUrl}/ee-static/checkout/pos-${posId}/images/swish-white.svg`}
        style={{ width: "100%", maxHeight: "2rem" }}
        alt="Swish"
        aria-hidden="true"
      />
    );
  if (method === INVOICE)
    return (
      <InvoiceIcon
        className={coverTotalCost && classes.grayedOut}
        language={language}
      />
    );
  else
    return <div className={coverTotalCost && classes.grayedOut}>{method}</div>;
};

const Payment = ({
  classes,
  sale,
  language,
  setItemExpired,
  getActiveMethods,
  dispatchPaymentMethod,
  posId,
  isOnePageCheckoutActive,
  selectedPaymentId,
  setSelectedPaymentId,
}) => {
  const isMobile = useMediaQuery("(max-width: 950px)");
  const { state } = useContext(AppContext);
  const { config } = useSelector((state) => ({
    config: state.config,
  }));

  useEffect(() => {
    if (!isOnePageCheckoutActive) {
      setSelectedPaymentId(null);
    }
  }, [isOnePageCheckoutActive]);

  const getChosenPayment = useMemo(() => {
    return getActiveMethods.find((el) => el.id === selectedPaymentId);
  }, [getActiveMethods, selectedPaymentId]);

  const label = (id, localName) => {
    switch (id) {
      case NETSEASY:
        return language.Payment_Add_NetsEasy;

      case SWEDBANKPAY:
        return language.Payment_Add_SwedbankPay;

      case SWISH:
        return language.Payment_Add_Swish;

      case KLARNA:
        return language.Payment_Add_Klarna;

      default:
        return localName;
    }
  };

  const paymentMethodsData =
    getActiveMethods.length > 0 &&
    getActiveMethods.reduce((acc, curr) => {
      if (
        curr &&
        curr.localName !== "Presentkort" &&
        curr.localName !== "GiftCertificate"
      ) {
        acc.push({
          value: curr.id,
          label: (
            <Center style={{ flexDirection: "column" }} p={10}>
              <Icon
                method={curr.id}
                posId={posId}
                chosen={getChosenPayment?.id === curr.id}
                language={language}
                coverTotalCost={state.giftCertificate?.coverTotalCost}
                classes={classes}
                config={config}
              />
              <Box mt={5}>{label(curr.id, curr.localName)}</Box>
            </Center>
          ),
        });
      }
      return acc;
    }, []);

  const paymentMethod = (event) => {
    setSelectedPaymentId(event);
    dispatchPaymentMethod(event);
  };

  const goBackUrl = localStorage.getItem("goBackUrl");

  const navigateBack = () => {
    const referrerPathname = document.referrer.split("/")?.[3];
    if (goBackUrl) {
      window.location.href = goBackUrl;
    } else if (referrerPathname === "") {
      window.location = `${document.referrer}ticketshop`;
    } else {
      window.location = document.referrer;
    }
  };

  useEffect(() => {
    const activeMethods = getActiveMethods.filter(
      (method) => method.id !== GIFTCERTIFICATE
    );
    if (activeMethods && activeMethods.length === 1) {
      paymentMethod(activeMethods?.[0]?.id);
    }
  }, []);

  return (
    <div>
      <div className={classes.headerWrapper}>
        <div>
          <h1 className={classes.title}>{language.Order}</h1>
          <span className={classes.subtitle}>{language.PaymentSubText}</span>
        </div>
        <div className={classes.buttonTimerWrapper}>
          {config?.discreetLoginButton && <User />}
          <Button variant={"default"} onClick={navigateBack}>
            {language.Back}
          </Button>
        </div>
      </div>
      <div className={classes.countdownWrapper}>
        <CountdownContent
          sale={sale}
          language={language}
          setItemExpired={setItemExpired}
        />
      </div>
      <AddDiscountGiftCertificate
        classes={classes}
        sale={sale}
        getActiveMethods={getActiveMethods}
      />
      {getActiveMethods.length > 0 && (
        <div className={classes.firstStep}>
          <div className={classes.titleWrapper}>
            <div className={classes.titleAsteriskWrapper}>
              <h2
                style={{ marginTop: 0 }}
                className={classes.title}
                id="paymentMethod"
              >
                {language.PaymentMethod}
              </h2>
              <span className="payment-asterisk">
                <Asterisk strokeWidth={4} />
              </span>
            </div>
            <span id="paymentMethodDescription" className={classes.subtitle}>
              {language.Step_Payment_Sub}
            </span>
          </div>
          <SegmentedControl
            value={selectedPaymentId}
            onChange={paymentMethod}
            data={paymentMethodsData}
            orientation={isMobile ? "vertical" : "horizontal"}
            fullWidth={isMobile ? true : false}
            color={"dark.5"}
            required={true}
            mt={16}
            role="radiogroup"
            aria-labelledby="paymentMethod"
            aria-describedby="paymentMethodDescription"
            className={classes.segmentedControl}
            disabled={state.giftCertificate?.coverTotalCost}
          />
        </div>
      )}
    </div>
  );
};

export default Payment;
